/**
 * InheritancePanel — drill-down panel showing both directions
 * of template inheritance for a list:
 *
 *   - Incoming: lists/templates this list expanded from (its `dependencies`)
 *   - Outgoing: lists/templates that include THIS list in their dependencies
 *
 * Empty sections render an explanation. Each section is a flat list
 * of links to the referenced lists.
 */
// TODO: show project/work name width checklist references
import { Panel, type RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { StackLink } from '@/app/navigation/StackLink';
import { Button } from '@/components/ui/button';
import { listQueries } from '@/features/lists/lists.queries';
import { ListProvider, useListContext } from '@/features/lists/context';
import { useQueries } from '@tanstack/react-query';
import { ArrowDownToLine, ArrowUpFromLine, GitBranch } from 'lucide-react';
import { useMemo, useState } from 'react';

const OUTGOING_BATCH_SIZE = 25;

export const InheritancePanel = ({
  params,
  ...panelProps
}: RoutedPanelProps) => {
  const listId = params.templateId || params.checklistId;

  if (!listId) {
    return (
      <Panel {...panelProps} title="Inheritance" icon={GitBranch}>
        <p className="text-muted-foreground text-sm">No list selected.</p>
      </Panel>
    );
  }

  return (
    <ListProvider listId={listId}>
      <Panel {...panelProps} title="Inheritance" icon={GitBranch}>
        <InheritancePanelContent />
      </Panel>
    </ListProvider>
  );
};

function InheritancePanelContent() {
  const { list, allLists } = useListContext();

  // Incoming: dependency lists already loaded in allLists (after primary).
  // Skip deleted deps — they are fetched so buildList can resolve, but
  // they should not appear in the UI as templates.
  const incoming = allLists.slice(1).filter((l) => !l.deleted);

  // Outgoing: fetch summaries by id from the dependants array.
  // Batched in OUTGOING_BATCH_SIZE chunks to avoid HTTP 414 on large lists.
  const dependantIds = list.dependants;
  const [visibleBatches, setVisibleBatches] = useState(1);

  const batches = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < dependantIds.length; i += OUTGOING_BATCH_SIZE) {
      out.push(dependantIds.slice(i, i + OUTGOING_BATCH_SIZE));
    }
    return out;
  }, [dependantIds]);

  const activeBatches = batches.slice(0, visibleBatches);
  const outgoingQueries = useQueries({
    queries: activeBatches.map((ids) =>
      listQueries.list({ id: `in.(${ids.join(',')})` }),
    ),
  });

  const outgoing = useMemo(
    () => outgoingQueries.flatMap((q) => q.data ?? []),
    [outgoingQueries],
  );

  const isLoading = outgoingQueries.some((q) => q.isLoading);
  const hasMore = visibleBatches < batches.length;
  const loadedCount = visibleBatches * OUTGOING_BATCH_SIZE;
  const remaining = Math.max(0, dependantIds.length - loadedCount);

  return (
    <div className="space-y-6">
      <Section
        icon={ArrowDownToLine}
        title="Incoming"
        description="Templates this list inherits content from."
        items={incoming.map((l) => ({
          id: l.id,
          name: l.name,
          isTemplate: l.werk_id == null,
        }))}
        emptyText="No incoming inheritance."
      />

      {dependantIds.length > 0 && (
        <Section
          icon={ArrowUpFromLine}
          title="Outgoing"
          description="Lists and templates that include this one."
          items={outgoing.map((l) => ({
            id: l.id,
            name: l.name,
            isTemplate: l.werk_id == null,
          }))}
          emptyText={isLoading ? 'Loading…' : ''}
          totalCount={dependantIds.length}
          footer={
            hasMore ? (
              <Button
                variant="outline"
                size="sm"
                className="mt-2 w-full"
                disabled={isLoading}
                onClick={() => setVisibleBatches((n) => n + 1)}
              >
                {isLoading
                  ? 'Loading…'
                  : `Load more (${Math.min(OUTGOING_BATCH_SIZE, remaining)} of ${remaining})`}
              </Button>
            ) : null
          }
        />
      )}
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  description,
  items,
  emptyText,
  totalCount,
  footer,
}: {
  icon: typeof ArrowDownToLine;
  title: string;
  description: string;
  items: { id: string; name: string; isTemplate: boolean }[];
  emptyText: string;
  totalCount?: number;
  footer?: React.ReactNode;
}) {
  const count =
    totalCount != null && totalCount !== items.length
      ? `${items.length} / ${totalCount}`
      : items.length;
  return (
    <section>
      <header className="mb-2 flex items-center gap-2">
        <Icon className="text-muted-foreground h-4 w-4" />
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-muted-foreground text-xs tabular-nums">
          {count}
        </span>
      </header>
      <p className="text-muted-foreground mb-2 text-xs">{description}</p>

      {items.length === 0 ? (
        <p className="text-muted-foreground text-sm italic">{emptyText}</p>
      ) : (
        <ul className="divide-border divide-y rounded-md border">
          {items.map((item) => (
            <li key={item.id}>
              <StackLink
                to={
                  item.isTemplate
                    ? `/templates/${item.id}`
                    : `/lists/${item.id}`
                }
                className="hover:bg-muted/40 flex items-center gap-2 px-3 py-2 text-sm no-underline"
              >
                <span className="flex-1 truncate">
                  {item.name || `Untitled (${item.id.slice(0, 8)})`}
                </span>
                <span className="text-muted-foreground text-xs">
                  {item.isTemplate ? 'template' : 'checklist'}
                </span>
              </StackLink>
            </li>
          ))}
        </ul>
      )}
      {footer}
    </section>
  );
}
